"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  Play,
  GitBranch,
  Users,
  Layers,
  Target,
  CheckCircle,
  RefreshCw,
} from "lucide-react";

const FlowchartNode = ({
  methodology,
  position,
  index,
  isVisible,
  onHover,
  isHovered,
}) => {
  const icons = [
    <Play className="w-6 h-6" />,
    <GitBranch className="w-6 h-6" />,
    <Users className="w-6 h-6" />,
    <Layers className="w-6 h-6" />,
    <Target className="w-6 h-6" />,
  ];

  const colors = [
    "from-emerald-400 to-teal-500",
    "from-blue-400 to-indigo-500",
    "from-purple-400 to-pink-500",
    "from-orange-400 to-red-500",
    "from-cyan-400 to-blue-500",
  ];

  return (
    <div
      className={`absolute transition-all duration-700 ${position} ${
        isVisible ? "opacity-100 scale-100" : "opacity-0 scale-75"
      }`}
      style={{ transitionDelay: `${index * 300}ms` }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
    >
      <div
        className={`relative group cursor-pointer transition-all duration-500 ${
          isHovered === index ? "scale-110 z-30" : "hover:scale-105 z-20"
        }`}
      >
        {/* Glow Effect */}
        <div
          className={`absolute inset-0 bg-gradient-to-r ${colors[index]} rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 scale-110`}
        />

        {/* Main Card */}
        <div
          className={`relative bg-white/15 backdrop-blur-md border-2 ${
            isHovered === index ? "border-white/50" : "border-white/20"
          } rounded-2xl p-6 w-72 shadow-2xl transition-all duration-500`}
        >
          {/* Step Number */}
          <div
            className={`absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-r ${colors[index]} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg border-4 border-white/20`}
          >
            {index + 1}
          </div>

          {/* Icon Header */}
          <div className="flex items-center mb-4">
            <div
              className={`p-3 bg-gradient-to-r ${colors[index]} text-white rounded-xl mr-4 shadow-lg`}
            >
              {icons[index]}
            </div>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          </div>

          {/* Content */}
          <h3 className="text-lg font-bold text-white mb-3 leading-tight">
            {methodology.title}
          </h3>
          <p className="text-white/80 text-sm leading-relaxed mb-4">
            {methodology.description}
          </p>

          {/* Benefits */}
          {/* <div className="space-y-2 mb-4">
            {methodology.benefits.slice(0, 2).map((benefit, benefitIndex) => (
              <div key={benefitIndex} className="flex items-center text-white/70 text-xs">
                <CheckCircle className="w-3 h-3 mr-2 text-green-400 flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div> */}

          {/* Status Indicator */}
          <div className="flex items-center justify-between pt-3 border-t border-white/20">
            <div className="flex items-center text-green-400 text-xs">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>Active</span>
            </div>
            <div className="text-white/60 text-xs">{methodology.duration}</div>
          </div>

          {/* Hover Overlay */}
          <div
            className={`absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
          />
        </div>
      </div>
    </div>
  );
};

const FlowArrow = ({ path, delay = 0, isVisible }) => {
  return (
    <svg
      className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-1000 z-10 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <defs>
        <marker
          id={`arrowhead-${delay}`}
          markerWidth="12"
          markerHeight="8"
          refX="11"
          refY="4"
          orient="auto"
        >
          <polygon points="0 0, 12 4, 0 8" fill="rgba(255, 255, 255, 0.7)" />
        </marker>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d={path}
        stroke="rgba(255, 255, 255, 0.6)"
        strokeWidth="3"
        fill="none"
        strokeDasharray="10 5"
        markerEnd={`url(#arrowhead-${delay})`}
        filter="url(#glow)"
        className="animate-pulse"
      />
    </svg>
  );
};

const MobileStackedCard = ({ methodology, index, isVisible }) => {
  const icons = [
    <Play className="w-6 h-6" />,
    <GitBranch className="w-6 h-6" />,
    <Users className="w-6 h-6" />,
    <Layers className="w-6 h-6" />,
    <Target className="w-6 h-6" />,
  ];

  const colors = [
    "from-emerald-400 to-teal-500",
    "from-blue-400 to-indigo-500",
    "from-purple-400 to-pink-500",
    "from-orange-400 to-red-500",
    "from-cyan-400 to-blue-500",
  ];

  return (
    <div
      className={`relative mb-8 transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
      }`}
      style={{ transitionDelay: `${index * 200}ms` }}
    >
      <div className="bg-white/15 backdrop-blur-md border-2 border-white/20 rounded-3xl p-6 mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center mb-6">
          <div
            className={`p-4 bg-gradient-to-r ${colors[index]} rounded-2xl shadow-lg mr-4`}
          >
            {icons[index]}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div
                className={`w-8 h-8 bg-gradient-to-r ${colors[index]} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`}
              >
                {index + 1}
              </div>
              <div className="text-white/60 text-sm">
                {methodology.duration}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <h3 className="text-xl font-bold text-white mb-4 leading-tight">
          {methodology.title}
        </h3>
        <p className="text-white/80 text-base leading-relaxed mb-6">
          {methodology.description}
        </p>

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          {methodology.benefits.map((benefit, benefitIndex) => (
            <div
              key={benefitIndex}
              className="flex items-center text-white/70 text-sm"
            >
              <CheckCircle className="w-4 h-4 mr-3 text-green-400 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* Status */}
        <div className="flex items-center justify-center pt-4 border-t border-white/20">
          <div className="flex items-center text-green-400 text-sm">
            <CheckCircle className="w-4 h-4 mr-2" />
            <span>Active Process</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function WorkFlow() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const methodologies = [
    {
      title: "Agile SDLC Methodology",
      description:
        "Vasta approaches software development with a disciplined management process that allows frequent alterations and iterative improvements throughout the development lifecycle.",
      benefits: [
        "Iterative Development Cycles",
        "Continuous Client Feedback",
        "Flexible Project Planning",
        "Risk Mitigation Strategies",
      ],
      duration: "2-4 weeks",
      position: "top-0 left-20",
    },
    {
      title: "DevOps Integration",
      description:
        "At Vasta, we seamlessly integrate Development and Operations through advanced DevOps methodologies, creating a unified workflow that accelerates deployment.",
      benefits: [
        "Continuous Integration/Deployment",
        "Automated Testing Pipelines",
        "Infrastructure as Code",
        "Enhanced Team Collaboration",
      ],
      duration: "Continuous",
      position: "top-0 right-20",
    },
    {
      title: "Scrum Development Framework",
      description:
        "Vasta deploys Scrum methodology for projects with highly emerging requirements and rapid changes, beginning with comprehensive sprint planning meetings.",
      benefits: [
        "Sprint-based Development",
        "Daily Standup Meetings",
        "Retrospective Analysis",
        "Adaptive Project Planning",
      ],
      duration: "1-3 weeks",
      position: "top-80 left-1/2 transform -translate-x-1/2",
    },
    {
      title: "Waterfall & Prototype Models",
      description:
        "Vasta expertise in Waterfall methodology provides a clear linear sequential development flow, while our Prototype approach creates functional demonstrations.",
      benefits: [
        "Clear Project Phases",
        "Prototype Validation",
        "Comprehensive Documentation",
        "Stakeholder Approval Gates",
      ],
      duration: "4-8 weeks",
      position: "bottom-10 left-18",
    },
    {
      title: "RAD, Spiral & JAD Models",
      description:
        "Vasta leverages Rapid Application Development for accelerated delivery, Spiral Model for comprehensive risk-focused development.",
      benefits: [
        "Rapid Prototyping",
        "Risk-focused Development",
        "Client Collaboration",
        "Quality Assurance Gates",
      ],
      duration: "2-6 weeks",
      position: "bottom-10 right-20",
    },
  ];

  // Linear flow paths: 1→2, 2→3, 3→4, 4→5
  const flowPaths = [
    // Agile (top-left) to DevOps (top-right)
    "M 400 140 Q 500 100 800 130",
    // DevOps (top-right) to Scrum (center)
    "M 800 180 Q 650 170 620 300",
    // Scrum (center) to Waterfall (bottom-left)
    "M 430 430 Q 400 420 310 580",
    // Waterfall (bottom-left) to RAD (bottom-right)
    "M 330 680 Q 550 740 800 700",
  ];

  return (
    <section className="relative py-16 lg:py-12 overflow-hidden bg-gradient-to-br from-[#0B1426] via-[#1719A8] to-[#0F1280]">
      <style>{`
        @keyframes flowPulse {
          0%, 100% { stroke-dashoffset: 0; }
          50% { stroke-dashoffset: 20; }
        }
        .flow-animation {
          animation: flowPulse 3s ease-in-out infinite;
        }
      `}</style>

      {/* Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
        <div className="absolute top-3/4 right-1/3 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
        <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-yellow-400 rounded-full animate-bounce" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-4 lg:mb-20 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="text-center mb-3 lg:mb-3 md:mb-3 sm:mb-4">
            <div className="inline-flex items-center px-6 py-2 bg-white/80 backdrop-blur-md border border-white/60 rounded-full mb-2 shadow-lg">
              <span className="w-2 h-2 bg-[#1719A8] rounded-full mr-3 animate-pulse"></span>
              <span
                style={{ color: "#1719A8" }}
                className="text-xs md:text-sm font-semibold uppercase tracking-wider"
              >
                Project Workflow
              </span>
            </div>
          </div>

          <h1 className="text-3xl lg:text-7xl font-black text-white mb-6 lg:mb-8 leading-tight px-4">
            Our Development
            <span className="block bg-gradient-to-r from-orange-400 via-yellow-400 to-green-400 bg-clip-text text-transparent">
              Process Flow
            </span>
          </h1>

          <p className="text-lg lg:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed px-4">
            Experience our intelligent automation workflow that seamlessly
            connects
            <span className="text-cyan-300 font-semibold"> methodologies</span>,
            <span className="text-green-300 font-semibold"> processes</span>,
            and
            <span className="text-yellow-300 font-semibold">
              {" "}
              delivery excellence
            </span>
            .
          </p>
        </div>

        {/* Desktop Flowchart */}
        <div className="hidden lg:block relative h-[800px] mb-6">
          {/* Flow Arrows */}
          {flowPaths.map((path, index) => (
            <FlowArrow
              key={index}
              path={path}
              delay={index * 500}
              isVisible={isVisible}
            />
          ))}

          {/* Methodology Nodes */}
          {methodologies.map((methodology, index) => (
            <FlowchartNode
              key={index}
              methodology={methodology}
              position={methodology.position}
              index={index}
              isVisible={isVisible}
              onHover={setHoveredNode}
              isHovered={hoveredNode}
            />
          ))}
        </div>

        {/* Mobile Stacked Cards */}
        <div className="lg:hidden">
          <div className="max-w-lg mx-auto">
            {methodologies.map((methodology, index) => (
              <MobileStackedCard
                key={index}
                methodology={methodology}
                index={index}
                isVisible={isVisible}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
